import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input } from './index';

const LeadMagnetModal = ({ isOpen, onClose, onSubscribe }) => {
    const { t } = useTranslation();
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const benefits = t('lead.benefits', { returnObjects: true }) || [];

    // Resetear estado al abrir/cerrar
    useEffect(() => {
        if (!isOpen) {
            setEmail('');
            setError('');
            setSuccess(false);
            setLoading(false);
        }
    }, [isOpen]);

    // Cerrar con ESC
    useEffect(() => {
        if (!isOpen) return;

        const handleEscape = (e) => {
            if (e.key === 'Escape' && !loading) {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, loading, onClose]);

    // Bloquear scroll cuando está abierto
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    const validateEmail = (email) => {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(email);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validar email
        if (!email.trim()) {
            setError(t('lead.invalidEmail'));
            return;
        }

        if (!validateEmail(email)) {
            setError(t('lead.invalidEmail'));
            return;
        }

        setError('');
        setLoading(true);

        try {
            if (onSubscribe) {
                const result = await onSubscribe(email);
                
                if (result.success) {
                    setSuccess(true);
                    // Cerrar después de 2 segundos
                    setTimeout(() => {
                        onClose();
                        setSuccess(false);
                    }, 2000);
                } else {
                    setError(result.error || t('lead.error'));
                }
            }
        } catch (err) {
            setError(err.message || t('lead.error'));
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={(e) => {
                if (e.target === e.currentTarget && !loading) {
                    onClose();
                }
            }}
        >
            <div 
                className="bg-white rounded-2xl shadow-2xl shadow-primary-900/10 max-w-lg w-full overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="relative p-6 sm:p-8">
                    <div className="absolute inset-0 bg-gradient-to-br from-primary-50/80 via-white to-secondary-50/70 pointer-events-none" />
                    <div className="relative space-y-6">
                        {/* Header */}
                        <div className="flex justify-between items-start gap-4">
                            <div className="space-y-3">
                                <span className="inline-flex items-center gap-2 bg-primary-600/10 text-primary-700 font-semibold uppercase tracking-[0.25em] text-[0.6rem] sm:text-xs rounded-full px-4 py-1.5 shadow-sm shadow-primary-200/50">
                                    {t('lead.offerBadge')}
                                </span>
                                <h2 className="text-2xl sm:text-3xl font-black text-gray-900 leading-tight">
                                    {t('lead.title')}
                                </h2>
                                <p className="text-gray-700 text-sm sm:text-base max-w-xl">
                                    {t('lead.description')}
                                </p>
                            </div>
                            <button
                                onClick={onClose}
                                disabled={loading}
                                className="text-gray-400 hover:text-gray-600 text-2xl leading-none disabled:opacity-50"
                            >
                                ×
                            </button>
                        </div>

                        {benefits.length > 0 && (
                            <div className="rounded-2xl border border-primary-100 bg-white/85 backdrop-blur-sm shadow-sm shadow-primary-100/40 p-4 sm:p-5">
                                <h3 className="text-xs sm:text-sm font-semibold uppercase tracking-widest text-primary-600 mb-3">
                                    {t('lead.benefitsTitle')}
                                </h3>
                                <ul className="space-y-2">
                                    {benefits.map((benefit, index) => (
                                        <li key={index} className="flex items-start gap-3 text-sm sm:text-base text-gray-700">
                                            <span className="flex-shrink-0 mt-0.5 text-primary-600">✦</span>
                                            <span>{benefit}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Form */}
                        {!success ? (
                            <form onSubmit={handleSubmit} className="space-y-5">
                                <Input
                                    type="email"
                                    label={t('lead.emailLabel')}
                                    value={email}
                                    onChange={(e) => {
                                        setEmail(e.target.value);
                                        setError('');
                                    }}
                                    placeholder={t('lead.emailPlaceholder')}
                                    required
                                    disabled={loading}
                                    className="w-full"
                                />

                                {error && (
                                    <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md text-sm">
                                        {error}
                                    </div>
                                )}

                                <div className="flex flex-col gap-4">
                                    <div className="flex justify-end gap-3">
                                        <Button
                                            type="button"
                                            variant="secondary"
                                            onClick={onClose}
                                            disabled={loading}
                                        >
                                            {t('common.cancel')}
                                        </Button>
                                        <Button
                                            type="submit"
                                            variant="primary"
                                            disabled={loading || !email.trim()}
                                            loading={loading}
                                            className="whitespace-nowrap"
                                        >
                                            {t('lead.subscribeButton')}
                                        </Button>
                                    </div>
                                    <p className="text-xs text-gray-500 text-center">
                                        {t('lead.helper')}
                                    </p>
                                </div>
                            </form>
                        ) : (
                            <div className="text-center py-6 space-y-4">
                                <div className="flex items-center justify-center">
                                    <svg 
                                        className="w-16 h-16 text-green-500" 
                                        fill="none" 
                                        stroke="currentColor" 
                                        viewBox="0 0 24 24"
                                    >
                                        <path 
                                            strokeLinecap="round" 
                                            strokeLinejoin="round" 
                                            strokeWidth={2} 
                                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" 
                                        />
                                    </svg>
                                </div>
                                <div className="space-y-2">
                                    <p className="text-lg font-semibold text-gray-900">
                                        {t('lead.success')}
                                    </p>
                                    <p className="text-sm text-gray-600 max-w-xs mx-auto">
                                        {t('lead.successMessage')}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LeadMagnetModal;

